import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { ChevronDownIcon, Check } from "../icons";
import { cn, } from "../../../types/shared/selectDropdown";
import "../../../styles/shared/selectDropdown";
const SelectContext = React.createContext(undefined);
const useSelectContext = () => {
    const context = React.useContext(SelectContext);
    if (!context)
        throw new Error("Select components must be used within a Select");
    return context;
};
const Select = ({ children, value, onValueChange, defaultValue, disabled = false, }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedValue, setSelectedValue] = React.useState(value || defaultValue || "");
    const [selectedLabel, setSelectedLabel] = React.useState("");
    const triggerRef = React.useRef(null);
    const contentRef = React.useRef(null);
    React.useEffect(() => {
        if (value !== undefined)
            setSelectedValue(value);
    }, [value]);
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (triggerRef.current &&
                contentRef.current &&
                !triggerRef.current.contains(event.target) &&
                !contentRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [isOpen]);
    const handleSelect = (value, label) => {
        setSelectedValue(value);
        setSelectedLabel(label);
        if (onValueChange) {
            onValueChange(value);
        }
        setIsOpen(false);
    };
    return (_jsx(SelectContext.Provider, { value: {
            isOpen,
            setIsOpen,
            selectedValue,
            selectedLabel,
            handleSelect,
            triggerRef,
            contentRef,
            disabled,
        }, children: _jsx("div", { className: "select-container", children: children }) }));
};
const SelectTrigger = React.forwardRef(({ children, className, error = false, ...props }, ref) => {
    const { isOpen, setIsOpen, triggerRef, disabled } = useSelectContext();
    return (_jsxs("button", { ref: triggerRef, type: "button", className: cn("select-trigger", error && "select-trigger-error", className), onClick: () => !disabled && setIsOpen(!isOpen), disabled: disabled, "aria-expanded": isOpen, "aria-haspopup": "listbox", ...props, children: [children, _jsx(ChevronDownIcon, { className: cn("select-icon", error && "select-icon-error") })] }));
});
SelectTrigger.displayName = "SelectTrigger";
const SelectValue = ({ placeholder = "Select...", }) => {
    const { selectedLabel, selectedValue } = useSelectContext();
    return (_jsx("span", { className: cn("select-value", !selectedValue && "select-placeholder"), children: selectedLabel || placeholder }));
};
const SelectContent = React.forwardRef(({ children, className, ...props }, ref) => {
    const { isOpen, contentRef } = useSelectContext();
    if (!isOpen)
        return null;
    return (_jsx("div", { ref: contentRef, className: cn("select-content", className), role: "listbox", ...props, children: _jsx("div", { className: "select-viewport", children: children }) }));
});
SelectContent.displayName = "SelectContent";
const SelectItem = React.forwardRef(({ children, value, className, disabled = false, ...props }, ref) => {
    const { selectedValue, handleSelect } = useSelectContext();
    const isSelected = selectedValue === value;
    return (_jsxs("div", { ref: ref, className: cn("select-item", isSelected && "select-item-selected", disabled && "select-item-disabled", className), onClick: () => !disabled && handleSelect(value, children), role: "option", "aria-selected": isSelected, "data-disabled": disabled, ...props, children: [isSelected && (_jsx("span", { className: "select-item-indicator", children: _jsx(Check, {}) })), _jsx("span", { className: "select-item-text", children: children })] }));
});
SelectItem.displayName = "SelectItem";
const SelectGroup = ({ children, className }) => (_jsx("div", { className: cn("select-group", className), children: children }));
const SelectLabel = ({ children, className }) => (_jsx("div", { className: cn("select-label", className), children: children }));
const SelectSeparator = ({ className }) => (_jsx("div", { className: cn("select-separator", className) }));
export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator, };
